import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

import { AdminService } from '@generated/services/admin.service';
import { BenchmarkSummaryDto } from '@generated/models/benchmark-summary-dto';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';

/**
 * Админский список бенчмарков (LLM + STT). Live-source:
 * GET /api/Admin/benchmarks → BenchmarkSummaryDto[]. Свагер не отдаёт
 * DELETE-эндпойнт для бенчей — снятие бенча с прогонов пока делается
 * через PUT с пустым model_version_links (или на бэке напрямую).
 */
@Component({
  selector: 'app-benchmarks-list',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    ButtonModule,
    TableModule,
    InputTextModule,
    SelectModule,
    TagModule,
    TooltipModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    DeleteConfirmationComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './benchmarks-list.component.html',
  styleUrl: './benchmarks-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenchmarksListComponent implements OnInit {
  private adminService = inject(AdminService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  readonly benchmarks = signal<BenchmarkSummaryDto[]>([]);
  readonly isLoading = signal<boolean>(false);

  readonly searchQuery = signal<string>('');
  readonly typeFilter = signal<string>('');

  // Delete flow — используем шарный <app-delete-confirmation>. Держим
  // pending-цель отдельно чтобы confirm-хендлер знал что удалять.
  readonly deleteVisible = signal<boolean>(false);
  readonly isDeleting = signal<boolean>(false);
  readonly deleteTarget = signal<BenchmarkSummaryDto | null>(null);

  // Run-in-progress индикатор per-benchmark id — чтобы одновременно жать
  // Run на разных строках было безопасно.
  readonly runningIds = signal<Set<string>>(new Set());
  private confirmationService = inject(ConfirmationService);

  readonly typeOptions = [
    { label: $localize`:@@benchmarks.type.all:Все типы`, value: '' },
    { label: 'LLM', value: 'llm' },
    { label: 'STT', value: 'stt' },
  ];

  readonly filtered = computed<BenchmarkSummaryDto[]>(() => {
    const q = this.searchQuery().trim().toLowerCase();
    const type = this.typeFilter().toLowerCase();
    return this.benchmarks().filter(b => {
      const typeOk = !type || (b.model_type_code || '').toLowerCase() === type;
      const qOk = !q
        || (b.title || '').toLowerCase().includes(q)
        || (b.description || '').toLowerCase().includes(q);
      return typeOk && qOk;
    });
  });

  ngOnInit(): void {
    this.loadBenchmarks();
  }

  private loadBenchmarks(): void {
    this.isLoading.set(true);
    this.cdr.markForCheck();
    this.adminService.apiAdminBenchmarksGet().subscribe({
      next: (data) => {
        this.benchmarks.set(data ?? []);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load benchmarks:', error);
        this.notificationService.showError(
          $localize`:@@benchmarks.notify.loadFailed:Не удалось загрузить бенчмарки`
        );
        this.isLoading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onAddClick(): void {
    this.router.navigate(['/admin/benchmarks/new']);
  }

  onEditClick(b: BenchmarkSummaryDto): void {
    if (!b.id) return;
    this.router.navigate(['/admin/benchmarks', b.id, 'edit']);
  }

  /** Запуск бенча целиком: POST /benchmarks/{id}/runs (без versionId).
   *  Backend enqueue'ит по всем привязанным версиям одним parent-run'ом.
   *  Стандартный PrimeNG confirm — консистентно с другими Run-действиями
   *  в приложении. */
  onRunClick(b: BenchmarkSummaryDto, event?: Event): void {
    if (!b.id) return;
    const versionsCount = b.model_version_count ?? 0;
    this.confirmationService.confirm({
      target: event?.currentTarget as EventTarget | undefined,
      header: $localize`:@@benchmarks.list.run.confirm.header:Запустить бенч?`,
      message: versionsCount > 0
        ? $localize`:@@benchmarks.list.run.confirm.message:«${b.title}:title:» запустится на ${versionsCount}:versions: версиях моделей. Продолжить?`
        : $localize`:@@benchmarks.list.run.confirm.messageNoLinks:У «${b.title}:title:» ещё нет привязанных версий моделей. Прогон пройдёт вхолостую. Продолжить?`,
      icon: 'pi pi-bolt',
      acceptLabel: $localize`:@@benchmarks.list.run.accept:Запустить`,
      rejectLabel: $localize`:@@common.cancel:Отмена`,
      accept: () => this.runBenchmark(b.id!),
    });
  }

  private runBenchmark(id: string): void {
    // Помечаем running для UI (спиннер на кнопке). Запрос без body —
    // сервер возьмёт iterations из самой Benchmark-сущности.
    this.runningIds.update(s => new Set(s).add(id));
    this.adminService.apiAdminBenchmarksBenchmarkIdRunsPost({ benchmarkId: id, body: {} })
      .subscribe({
        next: (result) => {
          this.runningIds.update(s => { const n = new Set(s); n.delete(id); return n; });
          const runIds = result?.benchmark_run_ids ?? [];
          this.notificationService.showSuccess(
            runIds.length > 0
              ? $localize`:@@benchmarks.list.run.started:Прогон стартанул. Открывается история...`
              : $localize`:@@benchmarks.list.run.startedEmpty:Прогон зарегистрирован. Открывается история...`
          );
          this.router.navigate(['/admin/benchmarks', id, 'runs']);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to run benchmark:', error);
          this.runningIds.update(s => { const n = new Set(s); n.delete(id); return n; });
          this.notificationService.showError(
            $localize`:@@benchmarks.list.run.failed:Не удалось запустить бенч`
          );
          this.cdr.markForCheck();
        }
      });
  }

  /** Delete flow — открываем шарный <app-delete-confirmation> и держим
   *  target в отдельном сигнале. */
  onDeleteClick(b: BenchmarkSummaryDto): void {
    if (!b.id) return;
    this.deleteTarget.set(b);
    this.deleteVisible.set(true);
  }

  onDeleteConfirm(): void {
    const target = this.deleteTarget();
    if (!target?.id) return;
    this.isDeleting.set(true);
    this.adminService.apiAdminBenchmarksBenchmarkIdDelete({ benchmarkId: target.id })
      .subscribe({
        next: () => {
          this.isDeleting.set(false);
          this.deleteVisible.set(false);
          this.deleteTarget.set(null);
          this.benchmarks.update(list => list.filter(b => b.id !== target.id));
          this.notificationService.showSuccess(
            $localize`:@@benchmarks.list.deleted:Бенч удалён`
          );
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to delete benchmark:', error);
          this.isDeleting.set(false);
          this.notificationService.showError(
            $localize`:@@benchmarks.list.deleteFailed:Не удалось удалить бенч`
          );
          this.cdr.markForCheck();
        }
      });
  }

  onDeleteCancel(): void {
    if (this.isDeleting()) return;
    this.deleteVisible.set(false);
    this.deleteTarget.set(null);
  }

  isRunning(id: string | null | undefined): boolean {
    return !!id && this.runningIds().has(id);
  }

  typeSeverity(code: string | null | undefined): 'info' | 'success' | 'warn' | 'secondary' {
    switch ((code || '').toLowerCase()) {
      case 'llm': return 'info';
      case 'stt': return 'success';
      default: return 'secondary';
    }
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '';
    try { return new Date(value).toLocaleString(); } catch { return value; }
  }
}
